export type ManagedWorkspaceHistoryKey = "chat" | "painting";

export type ManagedWorkspaceConversationType =
  | "conversation"
  | "painting"
  | "paintDalle"
  | "paintNanoBanana";

export interface WorkspaceHistoryCreation {
  userId: string;
  workspace: ManagedWorkspaceHistoryKey;
  conversationId: string;
  conversationType: ManagedWorkspaceConversationType;
  title?: unknown;
  createdAt?: number;
}

export interface PendingWorkspaceHistoryRecord {
  identity: string;
  userId: string;
  workspace: ManagedWorkspaceHistoryKey;
  conversationId: string;
  conversationType: ManagedWorkspaceConversationType;
  title: string;
  createdAt: number;
}

export interface WorkspaceHistoryState {
  pendingRecords: PendingWorkspaceHistoryRecord[];
  refreshVersion: Record<ManagedWorkspaceHistoryKey, number>;
}

export interface PersistedWorkspaceHistoryRecord {
  conversationId: string;
  conversationType: ManagedWorkspaceConversationType;
}

export interface WorkspaceHistoryReconciliation {
  userId: string;
  workspace: ManagedWorkspaceHistoryKey;
  records: PersistedWorkspaceHistoryRecord[];
}

const validConversationTypes: Record<
  ManagedWorkspaceHistoryKey,
  ReadonlySet<ManagedWorkspaceConversationType>
> = {
  chat: new Set(["conversation"]),
  painting: new Set(["painting", "paintDalle", "paintNanoBanana"]),
};

const normalizeTitle = (
  title: unknown,
  workspace: ManagedWorkspaceHistoryKey,
) => {
  const fallback = workspace === "painting" ? "当前绘画" : "当前对话";
  if (typeof title !== "string") return fallback;
  return title.replace(/\s+/g, " ").trim().slice(0, 80) || fallback;
};

export const getWorkspaceHistoryIdentity = (
  userId: string,
  workspace: ManagedWorkspaceHistoryKey,
  conversationType: ManagedWorkspaceConversationType,
  conversationId: string,
) => `${userId.trim()}:${workspace}:${conversationType}:${conversationId.trim()}`;

export const createInitialWorkspaceHistoryState =
  (): WorkspaceHistoryState => ({
    pendingRecords: [],
    refreshVersion: {
      chat: 0,
      painting: 0,
    },
  });

export const upsertPendingWorkspaceHistory = (
  state: WorkspaceHistoryState,
  creation: WorkspaceHistoryCreation,
): WorkspaceHistoryState => {
  const userId = String(creation.userId || "").trim();
  const conversationId = String(creation.conversationId || "").trim();
  if (
    !userId ||
    !conversationId ||
    !validConversationTypes[creation.workspace]?.has(
      creation.conversationType,
    )
  ) {
    return state;
  }

  const identity = getWorkspaceHistoryIdentity(
    userId,
    creation.workspace,
    creation.conversationType,
    conversationId,
  );
  const pendingRecord: PendingWorkspaceHistoryRecord = {
    identity,
    userId,
    workspace: creation.workspace,
    conversationId,
    conversationType: creation.conversationType,
    title: normalizeTitle(creation.title, creation.workspace),
    createdAt: creation.createdAt ?? Date.now(),
  };

  return {
    pendingRecords: [
      pendingRecord,
      ...state.pendingRecords.filter((record) => record.identity !== identity),
    ],
    refreshVersion: {
      ...state.refreshVersion,
      [creation.workspace]: state.refreshVersion[creation.workspace] + 1,
    },
  };
};

export const reconcileWorkspaceHistory = (
  state: WorkspaceHistoryState,
  reconciliation: WorkspaceHistoryReconciliation,
): WorkspaceHistoryState => {
  const persistedIdentities = new Set(
    reconciliation.records.map((record) =>
      getWorkspaceHistoryIdentity(
        reconciliation.userId,
        reconciliation.workspace,
        record.conversationType,
        record.conversationId,
      ),
    ),
  );
  const pendingRecords = state.pendingRecords.filter(
    (record) =>
      record.userId !== reconciliation.userId ||
      record.workspace !== reconciliation.workspace ||
      !persistedIdentities.has(record.identity),
  );

  if (pendingRecords.length === state.pendingRecords.length) return state;
  return { ...state, pendingRecords };
};
